import { useState } from 'react'
import { Card } from '@/components/shared/Card'
import { Button } from '@/components/shared/Button'
import { RequestCapModal } from './RequestCapModal'
import type { PermanentRequestGeo } from '@/types/api'

interface PermanentGeoSectionProps {
  geos: PermanentRequestGeo[]
  capUnit?: 'usd' | 'conversions'
  requestPath?: string
  /** Set when the selected offer is admin-paused — the server refuses new cap_requests
   *  while true, so the request button is disabled here instead of only surfacing the
   *  409 after submit. */
  paused?: boolean
}

/** Non-public geos — unified geo access + permanent private cap request. */
export function PermanentGeoSection({ geos, capUnit = 'usd', requestPath, paused = false }: PermanentGeoSectionProps) {
  const [active, setActive] = useState<PermanentRequestGeo | null>(null)

  if (geos.length === 0) return null

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-text-primary mb-1">Request geo access & cap</h3>
      <p className="text-xs text-text-muted mb-3">
        Request access and a permanent daily cap on these geos. Once approved, your allocation
        persists until changed — it does not reset to the overflow pool at midnight UTC.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {geos.map((g) => (
          <Card key={g.id} className="!p-4 flex flex-col gap-2.5">
            <div>
              <span className="font-semibold text-sm">{g.geo_code}</span>
              {g.geo_name && (
                <span className="text-xs text-text-muted ml-2">{g.geo_name}</span>
              )}
            </div>
            <Button
              variant="secondary"
              className="w-full text-xs mt-auto"
              disabled={paused}
              onClick={() => setActive(g)}
            >
              Request access & cap
            </Button>
            {paused && <p className="text-[10px] text-amber-600 text-center">Offer is paused</p>}
          </Card>
        ))}
      </div>
      {active && (
        <RequestCapModal
          open
          onClose={() => setActive(null)}
          geoId={active.id}
          geoCode={active.geo_code}
          geoName={active.geo_name ?? active.geo_code}
          requestPath={requestPath}
          requestKind="permanent"
          unit={capUnit}
          title="Request geo access & cap"
        />
      )}
    </div>
  )
}
